import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import api from '../../services/api';
import type { Product } from '../../types';
import CustomerPicker, { PickedCustomer } from './CustomerPicker';
import { formatMoney } from './contact';
import { todayIso } from '../expenses/ExpenseDialog';

type Line = { product: Product; qty: number; price: number };

const METHODS = ['Efectivo', 'Transferencia', 'Mercado Pago', 'Tarjeta'];
const DUE_OPTIONS = [7, 15, 30, 60];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Venta o pedido cargado desde la PC, con las mismas reglas que "Vender" del celular
 * (precio pactado del cliente, cuenta corriente, exhibidores). Un pedido no descuenta stock
 * hasta que se marca entregado.
 */
const NewSaleDialog: React.FC<Props> = ({ open, onClose, onSaved }) => {
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [channel, setChannel] = useState('Mayorista');
  const [channels, setChannels] = useState<string[]>(['Mayorista']);
  const [productOptions, setProductOptions] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [prices, setPrices] = useState<Record<number, number>>({});
  const [lines, setLines] = useState<Line[]>([]);
  const [displays, setDisplays] = useState(0);
  const [delivered, setDelivered] = useState(false);
  const [estimated, setEstimated] = useState('');
  const [paidNow, setPaidNow] = useState(false);
  const [method, setMethod] = useState('Transferencia');
  const [dueInDays, setDueInDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCustomer(null); setLines([]); setDisplays(0); setDelivered(false); setEstimated('');
    setPaidNow(false); setMethod('Transferencia'); setDueInDays(30); setNotes(''); setError(null); setChannel('Mayorista');
    api.getChannelCosts()
      .then(r => setChannels(Array.from(new Set(['Mayorista', ...(r.channels || [])])).filter(c => c !== 'Celular')))
      .catch(() => setChannels(['Mayorista']));
  }, [open]);

  // Precio del cliente (pactado → su lista → lista por defecto); al cambiar de cliente se re-precia.
  useEffect(() => {
    if (!open) return;
    api.resolvePrices(customer?.id)
      .then(rows => {
        const map = Object.fromEntries(rows.map(r => [r.productId, r.price]));
        setPrices(map);
        setLines(prev => prev.map(l => ({ ...l, price: map[l.product.id] ?? l.price })));
      })
      .catch(() => setPrices({}));
  }, [customer, open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.getProducts({ page: 1, pageSize: 30, search: productSearch || undefined });
        setProductOptions((res.products || []).filter(p => p.isActive !== false));
      } catch { setProductOptions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [productSearch, open]);

  const total = useMemo(() => lines.reduce((s, l) => s + l.qty * l.price, 0), [lines]);

  const addProduct = (p: Product | null) => {
    if (!p) return;
    setLines(prev => prev.some(l => l.product.id === p.id)
      ? prev.map(l => (l.product.id === p.id ? { ...l, qty: l.qty + 1 } : l))
      : [...prev, { product: p, qty: 1, price: prices[p.id] ?? p.price }]);
    setProductSearch('');
  };
  const patch = (id: number, x: Partial<Line>) => setLines(prev => prev.map(l => (l.product.id === id ? { ...l, ...x } : l)));

  const submit = async () => {
    if (!lines.length) { setError('Agregá al menos un producto.'); return; }
    if (lines.some(l => l.qty <= 0)) { setError('Revisá las cantidades.'); return; }
    if (!paidNow && !customer) { setError('Para dejarla a cuenta elegí el cliente.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.quickSale({
        customerId: customer?.id,
        items: lines.map(l => ({ productId: l.product.id, quantity: l.qty, unitPrice: l.price })),
        paidNow,
        paymentMethod: method,
        dueInDays: paidNow ? undefined : dueInDays,
        displaysDelivered: displays,
        notes: notes.trim() || undefined,
        channel,
        delivered,
        estimatedDelivery: !delivered && estimated ? estimated : undefined,
      });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo registrar la venta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Nueva venta / pedido</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <div style={{ flex: 2 }}><CustomerPicker value={customer} onChange={setCustomer} label="Cliente" /></div>
            <TextField select label="Canal" value={channel} onChange={e => setChannel(e.target.value)} sx={{ flex: 1 }}>
              {channels.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Stack>

          <Autocomplete
            options={productOptions}
            value={null}
            inputValue={productSearch}
            onInputChange={(_, v, reason) => { if (reason !== 'reset') setProductSearch(v); }}
            onChange={(_, p) => addProduct(p)}
            getOptionLabel={p => p.name}
            filterOptions={x => x}
            renderOption={(props, p) => (
              <li {...props} key={p.id}>
                <Stack sx={{ width: '100%' }} direction="row" justifyContent="space-between">
                  <span>{p.name}</span>
                  <Typography variant="body2" color="text.secondary">{formatMoney(prices[p.id] ?? p.price)} · stock {p.currentStock}</Typography>
                </Stack>
              </li>
            )}
            renderInput={params => <TextField {...params} label="Agregar producto" placeholder="Buscar por nombre o SKU…" />}
          />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Cantidad</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Precio</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(l => (
                <TableRow key={l.product.id}>
                  <TableCell>{l.product.name}</TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={l.qty} onChange={e => patch(l.product.id, { qty: Number(e.target.value) || 0 })}
                      inputProps={{ style: { textAlign: 'right' }, min: 1 }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={l.price} onChange={e => patch(l.product.id, { price: Number(e.target.value) || 0 })}
                      inputProps={{ style: { textAlign: 'right' } }} />
                  </TableCell>
                  <TableCell align="right">{formatMoney(l.qty * l.price)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setLines(prev => prev.filter(x => x.product.id !== l.product.id))} aria-label="Quitar"><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {lines.length === 0 && (
                <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Buscá y agregá los productos.</Typography></TableCell></TableRow>
              )}
              {lines.length > 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="right"><b>Total</b></TableCell>
                  <TableCell align="right"><b>{formatMoney(total)}</b></TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <ToggleButtonGroup exclusive value={delivered ? 'now' : 'later'} onChange={(_, v) => v && setDelivered(v === 'now')} size="small">
              <ToggleButton value="later">Pedido (se entrega después)</ToggleButton>
              <ToggleButton value="now">Ya entregado</ToggleButton>
            </ToggleButtonGroup>
            {!delivered && (
              <TextField size="small" type="date" label="Entrega estimada (opcional)" value={estimated} onChange={e => setEstimated(e.target.value)}
                InputLabelProps={{ shrink: true }} inputProps={{ min: todayIso() }} />
            )}
            <TextField size="small" type="number" label="Exhibidores" value={displays} onChange={e => setDisplays(Math.max(0, Number(e.target.value) || 0))} sx={{ width: 120 }} />
          </Stack>
          {!delivered && (
            <Typography variant="caption" color="text.secondary">
              El stock se descuenta cuando lo marcás entregado (Ventas → Pedidos a entregar), con la fecha de entrega.
            </Typography>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <ToggleButtonGroup exclusive value={paidNow ? 'now' : 'account'} onChange={(_, v) => v && setPaidNow(v === 'now')} size="small">
              <ToggleButton value="account">A cuenta</ToggleButton>
              <ToggleButton value="now">Ya pagó</ToggleButton>
            </ToggleButtonGroup>
            {paidNow ? (
              <TextField select size="small" label="Medio de pago" value={method} onChange={e => setMethod(e.target.value)} sx={{ minWidth: 180 }}>
                {METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            ) : (
              <TextField select size="small" label="Vence en" value={dueInDays} onChange={e => setDueInDays(Number(e.target.value))} sx={{ minWidth: 140 }}>
                {DUE_OPTIONS.map(d => <MenuItem key={d} value={d}>{d} días</MenuItem>)}
              </TextField>
            )}
          </Stack>

          <TextField label="Nota (opcional)" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || lines.length === 0}>
          {saving ? 'Guardando…' : `${delivered ? 'Registrar venta' : 'Guardar pedido'} ${formatMoney(total)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewSaleDialog;
